import React, { useEffect, useState } from "react";
import {
  Users2,
  GraduationCap,
  BookOpen,
  Activity,
  ShieldCheck,
  TrendingUp,
  AlertTriangle,
  Sparkles,
  ArrowUpRight,
  FileText,
  Wallet,
  Bot,
  CheckCircle,
  Clock,
  BarChart3,
  Unlink,
  InboxIcon,
  Scale,
  FlaskConical,
  Users,
  Zap,
} from "lucide-react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/layout/Shell";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useInstitution } from "../context/InstitutionContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { AcademiaAIPanel } from "../components/shared/AcademiaAIPanel";
import { ARGOVBadge } from "../components/shared/ARGOVBadge";
import {
  ProgrammeManagerDashboard,
  RegistrarDashboard,
  CareerServicesDashboard,
} from "../components/dashboards/variants-ops";
import {
  ComplianceOfficerDashboard,
  AIGovernanceDashboard,
  TrainingManagerDashboard,
  HRWorkforceDashboard,
  LineManagerDashboard,
  ExecutiveDashboard,
  FacultyDashboard,
  StudentDashboard,
} from "../components/dashboards/variants-strategic";
import { EngineeringCollegeDashboard } from "../components/dashboards/variant-engineering";

function Kpi({ label, value, hint, icon: Icon, trend, testid }) {
  return (
    <div className="kpi-card" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="label-eyebrow">{label}</span>
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
      </div>
      <div className="mt-3 flex items-end justify-between gap-2">
        <div className="text-3xl font-semibold tracking-tight tabular-nums">{value}</div>
        {trend && (
          <div className="flex items-center gap-1 text-xs font-medium text-emerald-600">
            <TrendingUp className="h-3 w-3" />
            {trend}
          </div>
        )}
      </div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function trendSeries() { return []; } // removed — NOT_CONNECTED (N3-R: no time-series API)

export default function Dashboard() {
  const { current } = useInstitution();
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!current) return;
    api
      .get(`/dashboard/${current.id}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [current?.id]);

  if (!current) return null;
  const m = data?.metrics || current.metrics || {};

  // Role-specific landing screens
  const role = user?.role;
  if (role === "programme_manager")
    return <ProgrammeManagerDashboard inst={current} m={m} />;
  if (role === "registrar")
    return <RegistrarDashboard inst={current} m={m} />;
  if (role === "career_services")
    return <CareerServicesDashboard inst={current} m={m} />;
  if (role === "compliance_officer")
    return <ComplianceOfficerDashboard inst={current} m={m} />;
  if (role === "ai_governance_admin")
    return <AIGovernanceDashboard inst={current} m={m} />;
  if (role === "training_manager")
    return <TrainingManagerDashboard inst={current} m={m} />;
  if (role === "hr_workforce_planner")
    return <HRWorkforceDashboard inst={current} m={m} />;
  if (role === "line_manager")
    return <LineManagerDashboard inst={current} m={m} />;
  if (role === "executive_leadership")
    return <ExecutiveDashboard inst={current} m={m} />;
  if (role === "faculty" || role === "instructor")
    return <FacultyDashboard inst={current} m={m} />;
  if (role === "student")
    return <StudentDashboard inst={current} user={user} />;

  // Engineering College institutions (e.g. Vaagdevi) get a placement-first
  // dashboard for the leadership / admin personas instead of the canonical
  // admin layout.
  const isEngColl = current.type === "Engineering College";
  if (
    isEngColl &&
    ["super_admin", "institution_admin", "dean", "executive_leadership"].includes(role)
  ) {
    return <EngineeringCollegeDashboard inst={current} m={m} user={user} />;
  }

  // Default admin/dean/super_admin view — enhanced University Home (NAV-X4)
  return <AdminUniversityHome current={current} m={m} user={user} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN UNIVERSITY HOME — 5-block enhanced dashboard (NAV-X4)
// ─────────────────────────────────────────────────────────────────────────────
function SnapshotCard({ label, value, icon: Icon, iconClass = "text-muted-foreground", state = "LIVE", to, testid }) {
  const isEmpty = value === null || value === undefined || value === "—";
  const displayState = isEmpty ? state : "LIVE";

  const inner = (
    <div
      className={`flex flex-col gap-1 rounded-xl border p-4 transition-colors ${
        to ? "hover:bg-muted/40 cursor-pointer" : ""
      } ${displayState === "NOT_CONNECTED" ? "border-dashed bg-muted/10" : "bg-card border-border"}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
        {Icon && <Icon className={`h-4 w-4 shrink-0 ${iconClass}`} strokeWidth={1.5} />}
      </div>
      {displayState === "NOT_CONNECTED" ? (
        <div className="flex items-center gap-1.5 mt-1">
          <Unlink className="h-3.5 w-3.5 text-muted-foreground/40" />
          <span className="text-xs text-muted-foreground/60">NOT_CONNECTED</span>
        </div>
      ) : displayState === "NO_DATA" ? (
        <div className="flex items-center gap-1.5 mt-1">
          <InboxIcon className="h-3.5 w-3.5 text-muted-foreground/40" />
          <span className="text-xs text-muted-foreground/60">No data yet</span>
        </div>
      ) : (
        <div className="text-2xl font-semibold tracking-tight tabular-nums mt-0.5">{value}</div>
      )}
    </div>
  );

  return to ? <Link to={to}>{inner}</Link> : inner;
}

function AttentionItem({ label, value, state = "LIVE", to, colorClass = "text-amber-600", testid }) {
  const isEmpty = value === null || value === undefined || value === "—";
  const displayState = isEmpty ? state : "LIVE";

  const inner = (
    <div
      className={`flex items-center justify-between gap-3 rounded-lg border px-4 py-3 ${
        to ? "hover:bg-muted/40 cursor-pointer" : ""
      } ${displayState === "NOT_CONNECTED" ? "border-dashed bg-muted/10" : "bg-card border-border"}`}
      data-testid={testid}
    >
      <span className="text-sm text-muted-foreground">{label}</span>
      {displayState === "NOT_CONNECTED" ? (
        <span className="text-xs text-muted-foreground/50 flex items-center gap-1">
          <Unlink className="h-3 w-3" />NOT_CONNECTED
        </span>
      ) : (
        <span className={`text-sm font-semibold tabular-nums ${colorClass}`}>{value}</span>
      )}
    </div>
  );

  return to ? <Link to={to}>{inner}</Link> : inner;
}

function AdminUniversityHome({ current, m, user }) {
  const [financeKpi, setFinanceKpi] = useState(null);
  const [ax1Runs, setAx1Runs] = useState(null);
  const [loadingExtra, setLoadingExtra] = useState(true);

  useEffect(() => {
    if (!current?.id) return;
    Promise.all([
      api.get(`/finance/kpi/institutional?institution_id=${current.id}`).catch(() => null),
      api.get(`/ax1/runs/${current.id}`).catch(() => null),
    ]).then(([kpiRes, ax1Res]) => {
      setFinanceKpi(kpiRes?.data || null);
      setAx1Runs(ax1Res?.data || null);
      setLoadingExtra(false);
    });
  }, [current?.id]);

  const fkpi = financeKpi || {};
  const pendingRuns = Array.isArray(ax1Runs)
    ? ax1Runs.filter((r) => r.status === "PROPOSED" || r.status === "PENDING").length
    : null;

  return (
    <div data-testid="dashboard-page" className="min-w-0 overflow-x-hidden">
      <PageHeader
        eyebrow="University Home"
        title={`${current.short_name}`}
        description={current.description || `${current.type} · ${current.country || ""}`}
        actions={
          <>
            <ARGOVBadge type="AI_GOVERNED" />
            <Badge variant="outline" className="gap-1.5">
              <span className="dot-pulse" />
              {m.ai_sessions != null ? m.ai_sessions.toLocaleString() : "—"} AI sessions
            </Badge>
            <Link to="/exec-briefing">
              <Button size="sm" variant="outline" className="gap-1.5" data-testid="exec-briefing-btn">
                <FileText className="h-3.5 w-3.5" /> Executive Briefing
              </Button>
            </Link>
          </>
        }
      />

      <div className="p-6 lg:p-8">
        {/* Two-column layout: main blocks + AI panel */}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-6">

          {/* ── LEFT: main blocks ─────────────────────────────────────────── */}
          <div className="space-y-6">

            {/* ── Block 1: Institution Snapshot ─────────────────────────── */}
            <section data-testid="dashboard-block-snapshot">
              <div className="label-eyebrow mb-3">Institution Snapshot</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <SnapshotCard
                  label="Students"
                  value={m.students}
                  icon={Users2}
                  iconClass="text-blue-500"
                  state={m.students ? "LIVE" : "NO_DATA"}
                  to="/core/students"
                  testid="snap-students"
                />
                <SnapshotCard
                  label="Faculty"
                  value={m.faculty}
                  icon={GraduationCap}
                  iconClass="text-indigo-500"
                  state={m.faculty ? "LIVE" : "NOT_CONNECTED"}
                  to="/people"
                  testid="snap-faculty"
                />
                <SnapshotCard
                  label="Retention"
                  value={m.completion_rate != null ? `${m.completion_rate}%` : null}
                  icon={TrendingUp}
                  iconClass="text-emerald-500"
                  state={m.completion_rate != null ? "LIVE" : "NOT_CONNECTED"}
                  testid="snap-retention"
                />
                <SnapshotCard
                  label="Academic Risk"
                  value={m.at_risk}
                  icon={AlertTriangle}
                  iconClass="text-amber-500"
                  state={m.at_risk != null ? "LIVE" : "NO_DATA"}
                  to="/core/students"
                  testid="snap-at-risk"
                />
                <SnapshotCard
                  label="Research Projects"
                  value={null}
                  icon={FlaskConical}
                  iconClass="text-purple-400"
                  state="NOT_CONNECTED"
                  testid="snap-research"
                />
                <SnapshotCard
                  label="Governed AI Actions"
                  value={m.ai_sessions != null ? m.ai_sessions.toLocaleString() : null}
                  icon={Sparkles}
                  iconClass="text-primary"
                  state={m.ai_sessions != null ? "LIVE" : "NOT_CONNECTED"}
                  to="/ai"
                  testid="snap-ai-sessions"
                />
              </div>
            </section>

            {/* ── Block 2: Requires Your Attention ──────────────────────── */}
            <section data-testid="dashboard-block-attention">
              <div className="label-eyebrow mb-3">Requires Your Attention</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <AttentionItem
                  label="Pending approvals"
                  value={
                    fkpi.pending_count != null
                      ? `${fkpi.pending_count} items`
                      : null
                  }
                  state="NOT_CONNECTED"
                  to="/finance/approval-queue"
                  colorClass="text-amber-600"
                  testid="attn-pending-approvals"
                />
                <AttentionItem
                  label="High-risk students"
                  value={m.at_risk != null ? `${m.at_risk} students` : null}
                  state={m.at_risk != null ? "LIVE" : "NO_DATA"}
                  to="/core/students"
                  colorClass="text-red-600"
                  testid="attn-high-risk"
                />
                <AttentionItem
                  label="Finance exceptions"
                  value={null}
                  state="NOT_CONNECTED"
                  testid="attn-finance-exceptions"
                />
                <AttentionItem
                  label="AI actions awaiting approval"
                  value={pendingRuns != null ? `${pendingRuns} runs` : null}
                  state={pendingRuns != null ? "LIVE" : "NOT_CONNECTED"}
                  to="/ax1-demo"
                  colorClass="text-purple-600"
                  testid="attn-ai-approvals"
                />
              </div>
            </section>

            {/* ── Block 3: Performance ──────────────────────────────────── */}
            <section data-testid="dashboard-block-performance">
              <div className="label-eyebrow mb-3">Performance</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="rounded-xl border border-border bg-card p-4" data-testid="perf-collection-rate">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Collection Rate</div>
                  {fkpi.collection_rate != null ? (
                    <>
                      <div className="text-2xl font-semibold tabular-nums mt-1">
                        {fkpi.collection_rate.toFixed(1)}%
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">of fees billed</div>
                    </>
                  ) : (
                    <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground/60">
                      <Unlink className="h-3.5 w-3.5" />
                      {loadingExtra ? "Loading…" : "NOT_CONNECTED"}
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-border bg-card p-4" data-testid="perf-fees-billed">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Fees Billed</div>
                  {fkpi.fees_billed != null ? (
                    <>
                      <div className="text-2xl font-semibold tabular-nums mt-1">
                        {typeof fkpi.fees_billed === "number"
                          ? fkpi.fees_billed.toLocaleString()
                          : fkpi.fees_billed}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{fkpi.currency || current.currency || ""}</div>
                    </>
                  ) : (
                    <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground/60">
                      <Unlink className="h-3.5 w-3.5" />
                      {loadingExtra ? "Loading…" : "NOT_CONNECTED"}
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-border bg-card p-4" data-testid="perf-fees-collected">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Fees Collected</div>
                  {fkpi.fees_collected != null ? (
                    <>
                      <div className="text-2xl font-semibold tabular-nums mt-1">
                        {typeof fkpi.fees_collected === "number"
                          ? fkpi.fees_collected.toLocaleString()
                          : fkpi.fees_collected}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{fkpi.currency || current.currency || ""}</div>
                    </>
                  ) : (
                    <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground/60">
                      <Unlink className="h-3.5 w-3.5" />
                      {loadingExtra ? "Loading…" : "NOT_CONNECTED"}
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-border bg-card p-4" data-testid="perf-student-success">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Student Success</div>
                  {m.completion_rate != null ? (
                    <>
                      <div className="text-2xl font-semibold tabular-nums mt-1">{m.completion_rate}%</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Completion rate</div>
                    </>
                  ) : (
                    <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground/60">
                      <InboxIcon className="h-3.5 w-3.5" />
                      No data yet
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* ── Block 4: Today Across Campus ──────────────────────────── */}
            <section data-testid="dashboard-block-today">
              <div className="label-eyebrow mb-3">Today Across Campus</div>
              <div className="rounded-xl border border-dashed border-border bg-muted/10 flex flex-col items-center justify-center py-8 gap-2" data-testid="today-not-connected">
                <Activity className="h-8 w-8 text-muted-foreground/25" />
                <p className="text-sm text-muted-foreground">Real-time campus activity not connected</p>
                <p className="text-xs text-muted-foreground/50">No time-series campus event API available · Connect to enable live activity feed</p>
              </div>
            </section>

            {/* ── Block 5: AI + Governance ──────────────────────────────── */}
            <section data-testid="dashboard-block-ai-governance">
              <div className="label-eyebrow mb-3">AI + Governance</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">

                {/* Active agents */}
                <div className="rounded-xl border border-border bg-card p-4" data-testid="gov-active-agents">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Active Agents</div>
                    <Bot className="h-4 w-4 text-primary/60" strokeWidth={1.5} />
                  </div>
                  {ax1Runs != null ? (
                    <div className="text-2xl font-semibold tabular-nums">
                      {Array.isArray(ax1Runs)
                        ? ax1Runs.filter((r) => r.status === "IN_PROGRESS" || r.status === "RUNNING").length
                        : "—"}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground/60">
                      <Unlink className="h-3.5 w-3.5" />
                      {loadingExtra ? "Loading…" : "NOT_CONNECTED"}
                    </div>
                  )}
                </div>

                {/* Pending AI approvals */}
                <div className="rounded-xl border border-border bg-card p-4" data-testid="gov-pending-approvals">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Pending AI Approvals</div>
                    <CheckCircle className="h-4 w-4 text-amber-500" strokeWidth={1.5} />
                  </div>
                  {pendingRuns != null ? (
                    <div className="text-2xl font-semibold tabular-nums text-amber-600">{pendingRuns}</div>
                  ) : (
                    <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground/60">
                      <Unlink className="h-3.5 w-3.5" />
                      {loadingExtra ? "Loading…" : "NOT_CONNECTED"}
                    </div>
                  )}
                  {pendingRuns > 0 && (
                    <Link to="/ax1-demo" className="text-xs text-primary mt-1 inline-block hover:underline">Review →</Link>
                  )}
                </div>

                {/* ARGOV + governance status */}
                <div className="rounded-xl border border-border bg-card p-4" data-testid="gov-argov-status">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Governance Status</div>
                    <Scale className="h-4 w-4 text-emerald-500" strokeWidth={1.5} />
                  </div>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">ARGOV</span>
                      <span className="font-medium text-emerald-600">Active</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Audit level</span>
                      <span className="font-medium">{current.governance?.audit_level || "—"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Human approval</span>
                      <span className="font-medium">
                        {current.governance?.require_human_approval != null
                          ? (current.governance.require_human_approval ? "Required" : "Optional")
                          : "—"}
                      </span>
                    </div>
                  </div>
                  <Link to="/governance" className="text-xs text-primary mt-2 inline-block hover:underline">
                    View evidence →
                  </Link>
                </div>
              </div>
            </section>

          </div>{/* end LEFT */}

          {/* ── RIGHT: Academia AI Panel ──────────────────────────────────── */}
          <div className="flex flex-col gap-4" data-testid="dashboard-right-panel">
            <AcademiaAIPanel />

            {/* Tenant snapshot (moved from main area) */}
            <div className="rounded-xl border border-border bg-card p-4" data-testid="tenant-snapshot-panel">
              <div className="label-eyebrow mb-3">Institution Profile</div>
              <dl className="text-sm space-y-2">
                <div className="flex justify-between"><dt className="text-muted-foreground">Type</dt><dd className="font-medium">{current.type}</dd></div>
                <div className="flex justify-between"><dt className="text-muted-foreground">Country</dt><dd className="font-medium">{current.country}</dd></div>
                <div className="flex justify-between"><dt className="text-muted-foreground">Currency</dt><dd className="font-medium">{current.currency || "—"}</dd></div>
                <div className="flex justify-between"><dt className="text-muted-foreground">Compliance</dt><dd className="font-medium text-end">{current.compliance_framework || "—"}</dd></div>
                <div className="flex justify-between"><dt className="text-muted-foreground">Timezone</dt><dd className="font-medium font-mono text-xs">{current.timezone}</dd></div>
              </dl>
              <Link
                to="/institution-setup"
                className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                Open setup console <ArrowUpRight className="h-3 w-3" />
              </Link>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}


